import { interpJoin, convertSeriesBasis, convertBasis } from '../hooks/seriesMath';
import { METRIC_NATIVE_BASIS, ratioCeiling, isYieldMetric, PE_MIN, PE_MAX } from '../config/metricsConfig';
import { RANGE_SELECTOR_BUTTONS } from '../config/rangeSelectorConfig';

/**
 * Realized (past-only) net debt PER SHARE as of `dateTs`, interpolated from
 * the annual-granularity `netDebt` and `shares` series the backend already
 * FX-converts onto the price axis. This is the single discipline that makes
 * the Enterprise-Value numerator honest for a HISTORICAL backtest point: an
 * EV multiple's numerator is `marketCap + netDebt`, i.e. per share it's
 * `price + netDebt/shares`. We must NEVER back-project today's net debt onto
 * an old observation, so — exactly like computeRealizedNtmFromAnnual does for
 * the metric series — we truncate both series to reports AT-OR-BEFORE `nowTs`
 * FIRST, then interpolate to `ts` (using the same interpJoin the ratio chart
 * uses to move an annual figure smoothly between report dates, and matching
 * buildActualSeries' `EV = marketCap + netDebt` convention). Returns 0 when
 * there's no net-debt/shares data at all (i.e. EV collapses to market cap —
 * the safe, information-free default), never null: a missing net-debt series
 * should degrade EV to market cap, not make every point unplottable.
 *
 * `ts` is always <= `nowTs` at every historical call site (observation dates
 * are past prices), so the realized-only truncation can never actually
 * exclude the value we interpolate to here — but it's applied anyway for
 * consistency with the metric series' own realized-only handling and to stay
 * correct if a future caller ever passes a forward `ts`.
 */
export const realizedNetDebtPerShare = (netDebt, shares, dateTs, nowTs) => {
  if (!netDebt?.length || !shares?.length) return 0;
  const realizedNetDebt = netDebt.filter(([ts]) => ts <= nowTs);
  const realizedShares = shares.filter(([ts]) => ts <= nowTs);
  if (!realizedNetDebt.length || !realizedShares.length) return 0;
  const [[, nd]] = interpJoin([[dateTs, null]], realizedNetDebt);
  const [[, sh]] = interpJoin([[dateTs, null]], realizedShares);
  if (nd == null || sh == null || sh === 0) return 0;
  return nd / sh;
};

/**
 * Forward-multiple (NTM) vs subsequent-return backtest, computed entirely
 * from the raw price/metric series the backend already returns — no new
 * backend endpoint, no server-side regression. Generalized from EPS (P/E)
 * to any of the app's 8 comparison metrics — for a metric like revenue the
 * "multiple" plotted is a P/S, not a P/E, but the mechanics are the same.
 *
 * Methodology (decided with product owner — do not "improve" without
 * re-confirming): for every HISTORICAL date we backtest, we do NOT claim to
 * know what analysts thought the forward multiple was at the time (that
 * history isn't in this dataset). Instead we use the REALIZED actual NTM
 * figure. For EPS specifically, that's the sum of the 4 quarterly actuals
 * that really did come next (see computeRealizedNtmEps below — EPS has
 * quarterly precision no other metric has). For every other metric, which
 * only has annual granularity, it's the same interpolation the ratio chart
 * already uses to move a metric smoothly between its annual report dates
 * (see computeRealizedNtmFromAnnual below). A date whose realized NTM figure
 * isn't determinable yet is simply not plottable and is skipped; we never
 * estimate/fake it. Only the single "current/now" point is a genuine
 * forward-looking estimate (blended analyst consensus), because that one is
 * inherently forward-looking by nature and real consensus already exists
 * for it.
 *
 * `period` ('ntm', default, or 'ttm') selects which multiple vintage feeds
 * the x-axis, per a follow-up product decision: TTM (trailing twelve
 * months) is the realized-only mirror image of NTM — it sums/reads the 4
 * quarters (or annual-interpolated year) ending AT-OR-BEFORE each backtest
 * date rather than the 4 (or 1 year) coming after it (see
 * computeRealizedTtmEps/computeRealizedTtmFromAnnual below). This is not
 * "extend the range" in the -1Y/+2Y/... vintage sense that was explicitly
 * rejected — see the design discussion this shipped from — it's a
 * *bias-diagnostic* pairing: NTM's historical points already use realized
 * hindsight earnings (a form of look-ahead bias baked in by construction,
 * since no historical analyst-estimate data exists to use instead), while
 * TTM's points are fully knowable at the time. Critically, TTM's live
 * "current/now" point is ALSO fully realized (computeRealizedTtmEps/
 * computeRealizedTtmFromAnnual evaluated at `nowTs` itself) — unlike NTM's
 * live point, TTM never touches an analyst estimate anywhere on the chart.
 * Comparing the two periods' regression fit is how a user checks whether
 * NTM's mean-reversion signal is a real, tradeable phenomenon or partly a
 * hindsight artifact.
 */

/** Backtest horizons, in months, keyed by the UI's horizon selector. */
export const TIME_HORIZONS = { '2Y': 24, '5Y': 60, '10Y': 120, '15Y': 180 };

/**
 * Evaluation-window options for the "how far back do we draw backtest
 * observations from" selector — a DIFFERENT axis than TIME_HORIZONS (which
 * is the *forward* holding period each observation looks ahead). Derived
 * from the same RANGE_SELECTOR_BUTTONS the two stock charts already use, so
 * the year set (10/15/20/25/30 + All) can't drift out of sync with them.
 * Keyed by the button `text` ('10y'…'All'); value is the lookback in years,
 * with `null` meaning "All" (no lower bound). The main charts' 5y button is
 * intentionally dropped here: a 5-year window can't feed even the shortest
 * useful backtest once a forward horizon is subtracted.
 */
export const LOOKBACK_WINDOWS = RANGE_SELECTOR_BUTTONS.reduce((acc, b) => {
  if (b.type === 'all') acc.All = null;
  else if (b.count >= 10) acc[`${b.count}Y`] = b.count;
  return acc;
}, {});

/** Default lookback: "All" history, matching the charts' widest option. */
export const DEFAULT_LOOKBACK = 'All';

/** The multiple-vintage options for this card's period selector — see the
 * module docstring's `period` section for what distinguishes them. */
export const PERIOD_OPTIONS = [
  { value: 'ntm', label: 'NTM' },
  { value: 'ttm', label: 'TTM' },
];

/** Below this many valid (forwardMultiple, subsequentReturn) pairs, the fit
 * is too noisy to trust — surfaced to the UI via `isInsufficientData`, not a
 * reason to withhold the (still-computed) regression fields. */
export const MIN_VALID_PAIRS = 30;

/** Historical forward-multiple outlier bounds: `(PE_MIN, PE_MAX]`, re-exported
 * from metricsConfig.js (which owns them — see that file's own docstring for
 * why they live there, not here) so existing imports of `PE_MIN`/`PE_MAX`
 * from this module keep working unchanged. A multiple at or below zero means
 * the realized NTM denominator was negative/zero (not a meaningful
 * multiple); above PE_MAX is almost always a near-zero-denominator artifact
 * rather than a genuine valuation signal, and would otherwise dominate the
 * OLS fit. Per-metric, the actual applied ceiling is narrower — see
 * `ratioCeiling` usage in buildValuationDataPoints below. */
export { PE_MIN, PE_MAX };

/**
 * Collapses an arbitrary-cadence chronological series down to one point per
 * calendar month: the last point on or before that month's end. Walking the
 * series once and only emitting a month's point once the next month's data
 * is seen (plus a final flush) means each emitted point is, by
 * construction, the latest one known as of its month — no lookahead, no
 * interpolation, and no fabricated point for months with zero data.
 */
export const sampleEndOfMonth = (series) => {
  const out = [];
  let currentKey = null;
  let currentPoint = null;
  for (const point of series) {
    const d = new Date(point[0]);
    const key = d.getUTCFullYear() * 12 + d.getUTCMonth();
    if (currentKey !== null && key !== currentKey) out.push(currentPoint);
    currentKey = key;
    currentPoint = point;
  }
  if (currentPoint) out.push(currentPoint);
  return out;
};

/**
 * The realized NTM EPS as of `dateTs`: the sum of the 4 quarterly actuals
 * that actually reported after that date. Returns null — never an
 * estimate — when fewer than 4 such quarters exist yet, or when any of
 * those 4 has a null/missing EPS actual, per the realized-only methodology
 * above: a missing quarter must make the point unplottable, not silently
 * coerce to 0 (mirrors the backend's `compute_ttm`, which applies the same
 * "all values present or the whole figure is null" rule to the same kind of
 * quarterly data).
 */
export const computeRealizedNtmEps = (epsQuarterly, dateTs) => {
  const nextFour = epsQuarterly.filter(([ts]) => ts > dateTs).slice(0, 4);
  if (nextFour.length < 4) return null;
  if (nextFour.some(([, eps]) => eps == null)) return null;
  return nextFour.reduce((sum, [, eps]) => sum + eps, 0);
};

/**
 * The realized TTM EPS as of `dateTs`: the sum of the 4 quarterly actuals
 * that reported at-or-before that date — the backward mirror of
 * computeRealizedNtmEps above. Always fully realized by construction (it
 * only ever looks at the past relative to `dateTs`), so unlike the NTM
 * figure it needs no "don't estimate" guard beyond the same missing-quarter
 * checks: null when fewer than 4 prior quarters exist yet, or when any of
 * those 4 has a null/missing EPS actual.
 */
export const computeRealizedTtmEps = (epsQuarterly, dateTs) => {
  const prevFour = epsQuarterly.filter(([ts]) => ts <= dateTs).slice(-4);
  if (prevFour.length < 4) return null;
  if (prevFour.some(([, eps]) => eps == null)) return null;
  return prevFour.reduce((sum, [, eps]) => sum + eps, 0);
};

/**
 * Realized NTM figure at `dateTs` for a metric with only annual granularity
 * (everything except EPS, which has its own quarterly-precise
 * computeRealizedNtmEps above) — interpolates `metricPerShareSeries`
 * (already converted to per-share basis by the caller) at `dateTs` plus one
 * calendar year, using the same interpJoin the ratio chart already relies
 * on. Truncates to points at-or-before `nowTs` FIRST, and returns null if
 * the one-year-forward target would fall beyond the last such point —
 * never interpolating (or, worse, flat-extrapolating past the truncated
 * series' end) into estimate territory, since this must stay "realized,
 * never estimated" exactly like computeRealizedNtmEps.
 */
export const computeRealizedNtmFromAnnual = (metricPerShareSeries, dateTs, nowTs) => {
  const actualPoints = metricPerShareSeries.filter(([ts]) => ts <= nowTs);
  if (actualPoints.length < 2) return null;
  const target = new Date(dateTs);
  target.setUTCFullYear(target.getUTCFullYear() + 1);
  const targetTs = target.getTime();
  const lastActualTs = actualPoints[actualPoints.length - 1][0];
  if (targetTs > lastActualTs) return null;
  const [[, value]] = interpJoin([[targetTs, null]], actualPoints);
  return value;
};

/**
 * Realized TTM figure at `dateTs` for a metric with only annual granularity
 * — the backward mirror of computeRealizedNtmFromAnnual above, but NOT a
 * one-year-offset interpolation: an annual metric's interpolated value
 * already behaves like a smoothed trailing-twelve-month figure (it moves
 * continuously between one full-year report and the next), so "TTM as of
 * `dateTs`" is simply that series' interpolated value AT `dateTs` itself,
 * no offset applied. Truncates to points at-or-before `nowTs` first, same
 * "realized, never estimated" discipline as computeRealizedNtmFromAnnual,
 * even though a value at-or-before `dateTs` (itself always <= `nowTs` at
 * every call site) could never land in estimate territory anyway.
 */
export const computeRealizedTtmFromAnnual = (metricPerShareSeries, dateTs, nowTs) => {
  const actualPoints = metricPerShareSeries.filter(([ts]) => ts <= nowTs);
  if (actualPoints.length < 2) return null;
  const [[, value]] = interpJoin([[dateTs, null]], actualPoints);
  return value;
};

/**
 * The one genuinely forward-looking NTM EPS figure in this module: blends
 * the nearest current-FY (fy0) and next-FY (fy1) analyst-consensus
 * estimates by how far through the calendar year `nowTs` falls, so the NTM
 * window smoothly rolls from "mostly this FY" in January to "mostly next
 * FY" in December instead of jumping once a year. Falls back to the single
 * available estimate (or null) when there isn't a second FY estimate to
 * blend against.
 */
export const computeCurrentNtmEpsEstimate = (futureFyEstimates, nowTs) => {
  if (!futureFyEstimates.length) return null;
  if (futureFyEstimates.length === 1) return futureFyEstimates[0][1];

  const [, fy0] = futureFyEstimates[0];
  const [, fy1] = futureFyEstimates[1];
  const d = new Date(nowTs);
  const yearStart = Date.UTC(d.getUTCFullYear(), 0, 1);
  const yearEnd = Date.UTC(d.getUTCFullYear() + 1, 0, 1);
  const w = (nowTs - yearStart) / (yearEnd - yearStart);
  return fy0 * (1 - w) + fy1 * w;
};

/**
 * Converts a scalar current-point figure (in the metric's NATIVE basis,
 * e.g. total revenue, not per-share) to per-share using the LATEST shares
 * outstanding — the "current/now" point always plots against a per-share
 * price, regardless of metric. Factored out of resolveCurrentMultiplePerShare
 * so its NTM branch doesn't inline this conversion.
 */
const toCurrentPerShare = (metric, shares, value) => {
  if (value == null) return null;
  if (metric === 'eps') return value;
  const latestShares = shares.length ? shares[shares.length - 1][1] : null;
  if (latestShares == null) return null;
  return convertBasis(value, METRIC_NATIVE_BASIS[metric], 'perShare', latestShares);
};

/**
 * The current/now point's per-share multiple figure, for either period.
 * NTM blends analyst-consensus futureFyEstimates (computeCurrentNtmEpsEstimate)
 * then converts that NATIVE-basis blend to per-share via toCurrentPerShare
 * above. TTM instead calls the same realized-only functions the historical
 * backtest points use (computeRealizedTtmEps/computeRealizedTtmFromAnnual),
 * evaluated AT `nowTs` — no analyst estimate involved at all for TTM, so no
 * toCurrentPerShare conversion is needed either: computeRealizedTtmFromAnnual
 * is fed a series already converted to per-share (mirroring exactly what
 * buildValuationDataPoints does for every historical point).
 */
const resolveCurrentMultiplePerShare = ({
  period, metric, shares, epsQuarterly, metricSeries, futureFyEstimates, nowTs,
}) => {
  if (period === 'ttm') {
    if (metric === 'eps') return computeRealizedTtmEps(epsQuarterly, nowTs);
    const metricPerShareSeries = convertSeriesBasis(metricSeries, METRIC_NATIVE_BASIS[metric], 'perShare', shares);
    return computeRealizedTtmFromAnnual(metricPerShareSeries, nowTs, nowTs);
  }
  const blended = computeCurrentNtmEpsEstimate(futureFyEstimates, nowTs);
  return toCurrentPerShare(metric, shares, blended);
};

/**
 * Annualized (CAGR) return between two prices `months` apart. A plain
 * geometric-growth-rate formula, factored out because both the backtest
 * (buildValuationDataPoints) and callers computing ad-hoc horizons need the
 * exact same annualization convention.
 */
export const computeCagr = (startPrice, endPrice, months) => (
  (endPrice / startPrice) ** (12 / months) - 1
);

/**
 * Builds the backtest scatter: for every end-of-month price date `d` that
 * has both (a) a realized NTM figure for `metric` (EPS's 4 future quarterly
 * actuals, or another metric's annual-interpolated equivalent — see
 * computeRealizedNtmEps/computeRealizedNtmFromAnnual) and (b) an EOM price
 * exactly `horizonMonths` later, emits one
 * `{ date, forwardMultiple, subsequentReturn }` point. Looking up the future
 * price by calendar-month key (rather than by array offset) keeps this
 * correct even if the underlying price series has a data gap spanning a
 * whole month. Non-positive NTM values and out-of-band forwardMultiple (see
 * PE_MIN and the metric's own `ratioCeiling`) are dropped as outliers rather
 * than skewing the OLS fit.
 *
 * For a YIELD metric (dividends — see `isYieldMetric`), the plotted x-value
 * is the reciprocal framing: metric-per-share ÷ price (a fraction, e.g.
 * 0.03 = a 3% dividend yield), not price ÷ metric. It still lives on the
 * `forwardMultiple` field so the OLS fit and chart code stay
 * metric-agnostic. The ratio-scaled `(PE_MIN, ceiling]` outlier band
 * doesn't apply to a yield (a near-zero denominator here is the price, which
 * is never near zero, so there's no explosion to clamp) — only the shared
 * "must be positive" guard is kept.
 *
 * `period` ('ntm', default, or 'ttm') switches which realized figure feeds
 * the denominator — see the module docstring's `period` section.
 *
 * `valueBasis` ('marketCap', default, or 'enterpriseValue') switches the
 * multiple's NUMERATOR: under Market Cap the per-share numerator is just the
 * price; under Enterprise Value it's `price + realized net debt per share`
 * (EV per share), where the net debt is interpolated realized-only — see
 * `realizedNetDebtPerShare`. Both the label (P/E vs EV/E) and this math are
 * driven by the SAME single `valueBasis` value threaded from the card, so
 * they can never disagree. An EV multiple can go non-positive when a company
 * holds more cash than its market cap (net cash > market cap => EV < 0); a
 * non-positive EV multiple is meaningless and is dropped as an outlier, the
 * same spirit as the `forwardMultiple <= PE_MIN` guard. (`ratioCeiling` is
 * also keyed by `valueBasis`, since a metric's plausible EV-multiple ceiling
 * can differ from its market-cap one.)
 *
 * displayScale (Per Share / Total) is deliberately NOT a parameter here: a
 * valuation multiple is SCALE-INVARIANT (a P/E equals total market cap ÷
 * total net income equals price ÷ EPS), so per-share vs total changes
 * neither this card's numbers nor its labels. axisScale (Linear / Log) is
 * likewise absent: it governs the MAIN price chart's y-axis rendering, and
 * has no meaning for this card's numeric-multiple x-axis / %-return y-axis.
 */
export const buildValuationDataPoints = ({
  price, shares, netDebt, metric, epsQuarterly, metricSeries, horizonMonths, lookbackYears, nowTs, period = 'ntm', valueBasis = 'marketCap',
}) => {
  const asEnterpriseValue = valueBasis === 'enterpriseValue';
  const eomPrice = sampleEndOfMonth(price);
  // Future-price lookups (below) must still see the FULL end-of-month series
  // even under a limited lookback: an observation dated inside the window can
  // legitimately resolve its `horizonMonths`-later price outside it. Only the
  // set of observations we *iterate* (and thus feed the OLS fit) is clamped
  // to the window — see the `lookbackCutoffTs` guard in the loop.
  const priceByMonthKey = new Map(eomPrice.map(([ts, px]) => {
    const d = new Date(ts);
    return [d.getUTCFullYear() * 12 + d.getUTCMonth(), [ts, px]];
  }));
  const lookbackCutoffTs = lookbackYears == null
    ? null
    : Date.UTC(new Date(nowTs).getUTCFullYear() - lookbackYears, new Date(nowTs).getUTCMonth(), 1);

  const metricPerShareSeries = metric === 'eps'
    ? null
    : convertSeriesBasis(metricSeries, METRIC_NATIVE_BASIS[metric], 'perShare', shares);
  const asYield = isYieldMetric(metric);
  const ceiling = ratioCeiling(metric, valueBasis, 'ratio') ?? PE_MAX;

  const points = [];
  for (const [ts, px] of eomPrice) {
    if (lookbackCutoffTs != null && ts < lookbackCutoffTs) continue; // outside evaluation window
    const d = new Date(ts);
    const monthKey = d.getUTCFullYear() * 12 + d.getUTCMonth();
    const future = priceByMonthKey.get(monthKey + horizonMonths);
    if (!future) continue; // not enough future price history yet

    const realizedValue = period === 'ttm'
      ? (metric === 'eps' ? computeRealizedTtmEps(epsQuarterly, ts) : computeRealizedTtmFromAnnual(metricPerShareSeries, ts, nowTs))
      : (metric === 'eps' ? computeRealizedNtmEps(epsQuarterly, ts) : computeRealizedNtmFromAnnual(metricPerShareSeries, ts, nowTs));
    if (realizedValue == null || realizedValue <= 0) continue;

    // Under Enterprise Value, the per-share numerator is EV/share =
    // price + realized net debt per share (interpolated past-only — never
    // today's net debt back-projected onto this old point; see
    // realizedNetDebtPerShare). Under Market Cap it's just the price. Both
    // the numerator here and the P/E-vs-EV/E label are driven by the same
    // `valueBasis`, so they can't diverge.
    const perShareNumerator = asEnterpriseValue
      ? px + realizedNetDebtPerShare(netDebt, shares, ts, nowTs)
      : px;
    // An EV multiple can go non-positive when net cash exceeds market cap
    // (EV/share < 0) — a meaningless multiple, dropped as an outlier the
    // same way the PE_MIN guard drops a non-positive market-cap multiple.
    if (asEnterpriseValue && perShareNumerator <= 0) continue;

    // Yield metrics plot metric/numerator (a fraction — under EV this is an
    // "E/EV Yield" style: metric per share ÷ EV per share); everything else
    // plots the numerator/metric forward (or trailing) multiple. The ratio
    // outlier band only guards the multiple form — see buildValuationDataPoints'
    // docstring.
    const forwardMultiple = asYield ? realizedValue / perShareNumerator : perShareNumerator / realizedValue;
    if (!asYield && (forwardMultiple <= PE_MIN || forwardMultiple > ceiling)) continue;

    const [, futurePx] = future;
    points.push({
      date: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`,
      forwardMultiple,
      subsequentReturn: computeCagr(px, futurePx, horizonMonths),
    });
  }
  return points;
};

/**
 * Ordinary least squares of subsequentReturn on forwardMultiple.
 * Mean-centered sums (rather than the raw-sums formula) keep this
 * numerically stable at the multiple magnitudes this module deals with
 * (tens, not near-zero). Degenerate inputs (fewer than 2 points, or zero
 * variance in x — a vertical-line dataset OLS can't fit) return an inert
 * `{ slope: 0, intercept: 0, rSquared: 0 }` rather than NaN/throwing, since
 * callers always render *something* for the fit line.
 */
export const fitLinearRegression = (points) => {
  const n = points.length;
  if (n < 2) return { slope: 0, intercept: 0, rSquared: 0 };

  const xs = points.map((p) => p.forwardMultiple);
  const ys = points.map((p) => p.subsequentReturn);
  const meanX = xs.reduce((a, b) => a + b, 0) / n;
  const meanY = ys.reduce((a, b) => a + b, 0) / n;

  let sumXY = 0;
  let sumXX = 0;
  for (let i = 0; i < n; i += 1) {
    sumXY += (xs[i] - meanX) * (ys[i] - meanY);
    sumXX += (xs[i] - meanX) ** 2;
  }
  if (sumXX === 0) return { slope: 0, intercept: 0, rSquared: 0 };

  const slope = sumXY / sumXX;
  const intercept = meanY - slope * meanX;

  let ssRes = 0;
  let ssTot = 0;
  for (let i = 0; i < n; i += 1) {
    const predicted = slope * xs[i] + intercept;
    ssRes += (ys[i] - predicted) ** 2;
    ssTot += (ys[i] - meanY) ** 2;
  }
  // ssTot === 0 means every y is identical (and, given sumXX !== 0 above,
  // sumXY must also be 0, so slope is already 0) — report a flat rSquared
  // of 0 rather than 0/0.
  const rSquared = ssTot === 0 ? 0 : 1 - ssRes / ssTot;

  return { slope, intercept, rSquared };
};

/**
 * Signed correlation coefficient `r = sign(slope) * sqrt(rSquared)`, derived
 * from an already-computed regression result. `rSquared` alone only
 * measures fit strength/magnitude and discards direction; for this module's
 * valuation-vs-subsequent-return relationship, direction is economically
 * meaningful — a negative `r` ("richer valuation -> lower subsequent
 * return") is the textbook mean-reversion signal one would expect, while a
 * positive `r` of the same magnitude is a surprising/atypical one. Used by
 * the metric-correlation table (`useMetricCorrelationTable`) to show
 * direction alongside `rSquared`'s magnitude, rather than replacing it —
 * sorting/"best predictor" ranking still goes by `rSquared` (equivalently
 * `|r|`), since a strong fit in either direction is still a strong fit.
 * Returns exactly `0` (not `-0`) when `slope` is `0` — the degenerate case
 * `fitLinearRegression` itself already returns for fewer than 2 points or
 * zero-variance `x` — rather than fabricating a sign for a flat fit.
 */
export const signedCorrelation = ({ slope, rSquared }) => (
  slope === 0 ? 0 : Math.sign(slope) * Math.sqrt(rSquared)
);

/**
 * Top-level entry point: runs the realized-value backtest over `horizon`
 * for the selected `period` (NTM or TTM), fits the OLS line, and projects
 * today's return from today's current-point multiple. For NTM that current
 * point is an *estimate* (the one place this module uses one rather than a
 * realized figure); for TTM it's fully realized too — see the module-level
 * methodology note's `period` section.
 * `currentMultiple`/`projectedReturn` are null (not NaN) whenever there's no
 * price or no current-point figure to work from, so the UI can
 * distinguish "can't compute today's point" from "computed a zero".
 *
 * `horizonMonths` (optional) overrides the `TIME_HORIZONS[horizon]` lookup —
 * used by `useValuationRegression`'s adaptive "as much history as this
 * symbol actually has, up to 15Y" search, which needs an arbitrary
 * whole-year length, not one of the four fixed tab values `horizon` keys.
 *
 * `lookbackYears` (optional, default null = All) limits the *evaluation
 * window*: only observations dated within the past `lookbackYears` feed the
 * fit — an axis orthogonal to `horizon`/`horizonMonths` (the forward holding
 * period). See buildValuationDataPoints for the exact cutoff semantics.
 *
 * `period` (optional, default `'ntm'`) selects the multiple vintage — see
 * the module docstring's `period` section. The returned object echoes it
 * back as `period` so callers that just spread the result (e.g. the chart
 * config builder) get it for free.
 *
 * `valueBasis` (optional, default `'marketCap'`) selects the multiple's
 * numerator (Market Cap vs Enterprise Value) — see buildValuationDataPoints'
 * docstring. The "current/now" point mirrors the historical points: under EV
 * its numerator is the LATEST realized EV per share (latest price + latest
 * realized net debt per share), so the projected-return marker sits on the
 * same EV scale as the scatter. It's echoed back as `valueBasis` so callers
 * spreading the result get it for free.
 */
export const computeValuationRegression = ({
  price, shares, netDebt, metric, epsQuarterly, metricSeries, futureFyEstimates, horizon, horizonMonths, lookbackYears = null, nowTs, period = 'ntm', valueBasis = 'marketCap',
}) => {
  const months = horizonMonths ?? TIME_HORIZONS[horizon];
  const points = buildValuationDataPoints({ price, shares, netDebt, metric, epsQuarterly, metricSeries, horizonMonths: months, lookbackYears, nowTs, period, valueBasis });
  const { slope, intercept, rSquared } = fitLinearRegression(points);

  const latestPrice = price.length ? price[price.length - 1][1] : null;
  const currentMultiplePerShare = resolveCurrentMultiplePerShare({
    period, metric, shares, epsQuarterly, metricSeries, futureFyEstimates, nowTs,
  });
  // The current point's per-share numerator, mirroring the historical points:
  // EV/share (latest price + latest realized net debt per share) under EV,
  // else just the latest price. realizedNetDebtPerShare evaluated at `nowTs`
  // uses the latest realized net debt/shares by construction.
  const currentNumerator = latestPrice == null
    ? null
    : (valueBasis === 'enterpriseValue'
      ? latestPrice + realizedNetDebtPerShare(netDebt, shares, nowTs, nowTs)
      : latestPrice);
  const canComputeCurrent = currentNumerator != null && currentNumerator > 0
    && currentMultiplePerShare != null && currentMultiplePerShare > 0;
  // Mirror buildValuationDataPoints' x-value: yield metrics plot metric/numerator.
  const currentMultiple = !canComputeCurrent
    ? null
    : (isYieldMetric(metric) ? currentMultiplePerShare / currentNumerator : currentNumerator / currentMultiplePerShare);
  const projectedReturn = currentMultiple != null ? slope * currentMultiple + intercept : null;

  return {
    slope,
    intercept,
    rSquared,
    points,
    currentMultiple,
    projectedReturn,
    isInsufficientData: points.length < MIN_VALID_PAIRS,
    period,
    valueBasis,
  };
};
